import {EyeOutlined} from '@ant-design/icons';
import {Card, Modal, Timeline as _Timeline, Typography} from 'antd';
import {ProviderEvent, useProviderEventsQuery} from 'api/generated';
import {AnchorLink, EmailLink} from 'components';
import {Code} from 'components/Code';
import {formatDate} from 'lib/formaters';
import React, {useState} from 'react';
import {useRouteMatch} from 'react-router-dom';
import styled from 'styled-components';
import {useDebouncedCallback} from 'use-debounce';
import {getChangedKeys, prettifyEvent} from '../../../lib/utils';
import {eventMapping, getProviderEventDescription} from '../utils';

const {Paragraph} = Typography;

const Timeline = styled(_Timeline)`
  margin: 10px 0 0 3px !important;
  & .ant-timeline-item-last {
    padding-bottom: 0;
    & > .ant-timeline-item-content {
      min-height: auto;
    }
  }
`;

const Description = styled.div`
  padding: 0.2em 0.4em;
  margin: 0.2em 0;
  background: rgba(150, 150, 150, 0.1);
  border: 1px solid rgba(100, 100, 100, 0.2);
  border-radius: 3px;
`;

// Always put subscription.pending first
const eventSorter = (a: any, b: any) => {
  const diff = b.createdAt - a.createdAt;
  if (diff !== 0) return diff;
  return 0;
};

export const ProviderTimeline: React.FC = () => {
  const match = useRouteMatch<{providerId: string; accountId: string}>();
  const {providerId, accountId} = match.params;
  const [selectedEvent, setSelectedEvent] = useState<ProviderEvent | null>(null);
  const [isInfoOpen, setInfoOpen] = useState(false);

  const {data, loading, refetch} = useProviderEventsQuery({
    skip: !providerId,
    variables: {providerId: providerId, accountId}
  });

  useDebouncedCallback(refetch, 5000);

  const events =
    data?.providerEvents.items
      .slice()
      .sort(eventSorter)
      .map((item, i, arr) => ({
        ...item,
        changed: !!arr[i++]
          ? getChangedKeys(item, arr[i++]).map((key) => key.replace(/^object\./, ''))
          : []
      })) || [];

  const handleInfoClose = () => {
    setInfoOpen(false);
  };

  const getTitle = (event: ProviderEvent) => {
    const item = eventMapping[event.type];
    if (!item) return <></>;
    const details = (event as ProviderEvent).object?.traceDetails;
    if (details?.userEmail) {
      return (
        <>
          {item.title} by <EmailLink email={details.userEmail}>{details.userName}</EmailLink>
        </>
      );
    } else if (details?.source) {
      return (
        <>
          {item.title} ({details.source})
        </>
      );
    }
    return item.title;
  };

  return (
    <>
      <Card title={'Timeline'} size="small" loading={loading}>
        <Timeline>
          {events.map((event, i) => {
            const item = eventMapping[event.type];
            const description = getProviderEventDescription(event as ProviderEvent);
            return (
              <Timeline.Item
                key={i}
                dot={item?.icon ? <item.icon style={{color: item.color}} /> : null}>
                <Paragraph style={{marginBottom: 0}}>
                  {getTitle(event)}{' '}
                  <AnchorLink
                    onClick={() => {
                      setSelectedEvent(event);
                      setInfoOpen(true);
                    }}>
                    <EyeOutlined />
                  </AnchorLink>
                </Paragraph>
                {description && <Description>{description}</Description>}
                <Paragraph type="secondary" style={{fontSize: '12px', marginBottom: 0}}>
                  {formatDate(event.createdAt, 'X', true, true)}
                </Paragraph>
              </Timeline.Item>
            );
          })}
        </Timeline>
      </Card>
      <Modal
        title={'Event data'}
        width={800}
        open={isInfoOpen}
        footer={false}
        onCancel={handleInfoClose}>
        {selectedEvent && <Code>{prettifyEvent(selectedEvent)}</Code>}
      </Modal>
    </>
  );
};
